import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet } from "react-router-dom";
import { UndrMark, UndrWordmark } from "../brand/UndrMark";
import { safeGet } from "../lib/api";
import { fmtDate, parseDate, relTime } from "../lib/format";
import { IconBacktest, IconLogs, IconPortfolio, IconResults, IconSettings, IconSignOut } from "./Icons";

const NAV = [
  { to: "/app", label: "Results", icon: IconResults, end: true },
  { to: "/app/portfolio", label: "Portfolio", icon: IconPortfolio },
  { to: "/app/backtest", label: "Backtest", icon: IconBacktest },
  { to: "/app/logs", label: "Logs", icon: IconLogs },
  { to: "/app/settings", label: "Settings", icon: IconSettings },
];

function nextRunLabel(nextRun) {
  const d = parseDate(nextRun);
  if (!d) return null;
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const time = d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Next run today ${time}`;
  const tomorrow = new Date(now);
  tomorrow.setDate(now.getDate() + 1);
  if (d.toDateString() === tomorrow.toDateString()) return `Next run tomorrow ${time}`;
  return `Next run ${fmtDate(d)}`;
}

export default function AppShell() {
  const [status, setStatus] = useState(null);
  const [me, setMe] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const [s, m] = await Promise.all([safeGet("/api/status", null), safeGet("/api/me", null)]);
      if (!alive) return;
      setStatus(s);
      setMe(m);
    };
    load();
    const t = setInterval(load, 60000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const mode = status?.mode || me?.mode;
  const online = !!status;
  const dotColor = !online ? "var(--muted)" : status.state === "error" ? "var(--red)" : "#34D399";
  const modeLabel = mode === "cloud" ? "Cloud · GitHub Actions" : mode === "local" ? "Local · Flask" : "Backend offline";
  const next = nextRunLabel(status?.next_run);

  return (
    <div className="app-shell">
      <nav aria-label="Main" className="sidebar">
        <Link to="/" className="sidebar-brand" aria-label="Undr home">
          <UndrMark size={28} />
          <UndrWordmark size={22} />
        </Link>
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink key={to} to={to} end={end} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
            <Icon />
            {label}
          </NavLink>
        ))}
        <div className="spacer" style={{ flexGrow: 1 }} />
        {me && (me.email || me.name) && (
          <div className="sidebar-card" style={{ background: "transparent", padding: "8px 12px" }}>
            <div style={{ color: "var(--onSidebar)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={me.email}>
              {me.name || me.email}
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, color: "var(--onSidebarMuted)" }}>
              <span>{me.admin ? "Admin" : "Viewer"}</span>
              {mode === "cloud" && (
                <a
                  href="/cdn-cgi/access/logout"
                  style={{ color: "var(--onSidebarMuted)", display: "inline-flex", alignItems: "center", gap: 4, textDecoration: "none" }}
                >
                  <IconSignOut /> Sign out
                </a>
              )}
            </div>
          </div>
        )}
        <div className="sidebar-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--onSidebarMuted)" }}>
            <span className="dot" style={{ background: dotColor }} />
            {status?.state === "scanning" ? "Scanning…" : modeLabel}
          </div>
          <div style={{ color: "var(--onSidebar)" }}>
            {status?.last_scan ? `Last scan ${fmtDate(status.last_scan)}` : online ? "No scan yet" : "Could not reach /api/status"}
          </div>
          {next && (
            <div style={{ color: "var(--onSidebarMuted)" }} title={relTime(status.next_run)}>
              {next}
            </div>
          )}
        </div>
      </nav>
      <main className="main">
        <Outlet context={{ status, me }} />
      </main>
    </div>
  );
}
